export type FlightTypeCode = 'B' | 'PE' | 'PNE' | 'R' | 'T' | 'M' | 'C';

export interface FlightTypeMeta {
  code: FlightTypeCode;
  label: string;
  short: string;
  deductible: boolean;
  /** tailwind text/bg/ring classes for a badge */
  badge: string;
  /** tailwind gradient classes for a bar/fill */
  bar: string;
}

export const FLIGHT_TYPES: FlightTypeMeta[] = [
  { code: 'B', label: 'Business', short: 'Business', deductible: true, badge: 'bg-sky-500/15 text-sky-300 ring-sky-500/30', bar: 'from-sky-500 to-sky-400' },
  { code: 'PE', label: 'Personal · Entertainment', short: 'Personal (Ent.)', deductible: false, badge: 'bg-purple-500/15 text-purple-300 ring-purple-500/30', bar: 'from-purple-500 to-purple-400' },
  { code: 'PNE', label: 'Personal · Non-Entertainment', short: 'Personal (Non-Ent.)', deductible: true, badge: 'bg-indigo-500/15 text-indigo-300 ring-indigo-500/30', bar: 'from-indigo-500 to-indigo-400' },
  { code: 'R', label: 'Rental', short: 'Rental', deductible: false, badge: 'bg-slate-500/15 text-slate-300 ring-slate-500/30', bar: 'from-slate-500 to-slate-400' },
  { code: 'T', label: 'Training', short: 'Training', deductible: true, badge: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30', bar: 'from-emerald-500 to-emerald-400' },
  { code: 'M', label: 'Maintenance', short: 'Maintenance', deductible: true, badge: 'bg-amber-500/15 text-amber-300 ring-amber-500/30', bar: 'from-amber-500 to-amber-400' },
  { code: 'C', label: 'Charity', short: 'Charity', deductible: false, badge: 'bg-pink-500/15 text-pink-300 ring-pink-500/30', bar: 'from-pink-500 to-pink-400' },
];

export const FLIGHT_TYPE_MAP: Record<string, FlightTypeMeta> = Object.fromEntries(
  FLIGHT_TYPES.map((t) => [t.code, t])
);

/**
 * Flight-type code → the headcount field the API sends for it. BA Law's columns
 * G–M, and a DISPLAY mapping only: the counts themselves are derived server-side
 * from the manifest (`api/src/services/souls.ts`) and arrive on `headcount`.
 * Nothing here counts anything.
 */
export const SOULS_FIELDS: Array<{ code: FlightTypeCode; field: string }> = [
  { code: 'B', field: 'business_souls' },
  { code: 'PE', field: 'personal_entertainment_souls' },
  { code: 'PNE', field: 'personal_non_entertainment_souls' },
  { code: 'R', field: 'rental_souls' },
  { code: 'T', field: 'training_souls' },
  { code: 'M', field: 'maintenance_souls' },
  { code: 'C', field: 'charity_souls' },
];

export function flightTypeLabel(code: string): string {
  return FLIGHT_TYPE_MAP[code]?.label ?? code;
}

/* -------------------------------------------------------------------------- */
/* The manifest's two closed vocabularies                                      */
/*                                                                            */
/* Wording for the two columns on every soul. The API validates both against   */
/* the same sets and decides the headcount from `affiliation`; these are the   */
/* labels, the way FLIGHT_TYPES is the wording for the flight_type enum.       */
/* -------------------------------------------------------------------------- */

/** What a soul was doing aboard. Recorded because it is true; decides nothing. */
export type SoulRoleCode = 'PIC' | 'SIC' | 'instructor' | 'passenger';

export const SOUL_ROLES: Array<{ code: SoulRoleCode; label: string }> = [
  { code: 'PIC', label: 'Pilot in command' },
  { code: 'SIC', label: 'Second in command' },
  { code: 'instructor', label: 'Instructor' },
  { code: 'passenger', label: 'Passenger' },
];

/**
 * What a soul was to the operation. BA Law's headcount exclusions key on THIS,
 * not on the role: a hired ferry pilot and the co-owner are both PIC and land on
 * opposite sides of the count.
 */
export type SoulAffiliationCode =
  | 'owner' | 'co_owner' | 'employee' | 'business_guest' | 'family' | 'guest'
  | 'hired_pilot' | 'hired_instructor' | 'inspector' | 'lap_child';

export const SOUL_AFFILIATIONS: Array<{
  code: SoulAffiliationCode;
  label: string;
  /** Left out of BA Law's headcount — but never out of the manifest. */
  excluded: boolean;
}> = [
  { code: 'owner', label: 'Owner', excluded: false },
  { code: 'co_owner', label: 'Co-owner', excluded: false },
  { code: 'employee', label: 'Employee', excluded: false },
  { code: 'business_guest', label: 'Business guest', excluded: false },
  { code: 'family', label: 'Family', excluded: false },
  { code: 'guest', label: 'Personal guest', excluded: false },
  { code: 'hired_pilot', label: 'Hired professional pilot', excluded: true },
  { code: 'hired_instructor', label: 'Instructor (professional)', excluded: true },
  { code: 'inspector', label: 'Inspector / examiner / mechanic', excluded: true },
  { code: 'lap_child', label: 'Lap child (under 2)', excluded: true },
];

const AFFILIATION_MAP = Object.fromEntries(SOUL_AFFILIATIONS.map((a) => [a.code, a]));

export function affiliationLabel(code: string | null | undefined): string {
  return AFFILIATION_MAP[code || '']?.label ?? String(code ?? '—');
}

export function isExcludedAffiliation(code: string | null | undefined): boolean {
  return AFFILIATION_MAP[code || '']?.excluded === true;
}

export function soulRoleLabel(code: string | null | undefined): string {
  return SOUL_ROLES.find((r) => r.code === code)?.label ?? String(code ?? '—');
}
